/* eslint-disable react-refresh/only-export-components --
 * Deliberately a mixed module, matching components/Documents/tmf.tsx: the risk
 * fetch hook and the components that render its payload are one unit shared by
 * the portfolio site profile and the trial-level site Risk tab.
 */
/**
 * Shared site delay-risk pieces, used by both the portfolio site profile's Risk
 * tab and the trial-level site Risk tab's startup band.
 *
 * Convention throughout: a missing value is rendered as a gap with its reason,
 * never as a zero or a reassuring band. Sites the scorer had too little
 * evidence to score, and supporting metrics this data feed cannot produce, are
 * both real findings — they are what tells the reader which data is still owed.
 */

import { useQuery } from "@tanstack/react-query";
import { Table } from "antd";
import type { ColumnsType } from "antd/es/table";

import { apiFetchJson } from "@/utils/apiFetch";
import InfoTooltip from "@/components/UI/InfoTooltip";

export type RiskFactor = {
  code: string;
  label: string;
  /** The counts the factor was computed from, in words. */
  evidence?: string | null;
  /** This factor's share of the score, 0-1. */
  share?: number | null;
  value: number;
  weight: number;
  contribution: number;
  basis: string;
};

export type RiskMilestone = {
  trial_id: string;
  code: string;
  label: string;
  status: string;
  expected_date: string | null;
  actual_date: string | null;
  latency_days: number | null;
  expected_documents: number | null;
  collected_documents: number | null;
  derivation: string;
};

export type RiskOrganizationLocation = {
  site_key: string;
  site_name: string | null;
  city: string | null;
  country: string | null;
  score: number | null;
  classification: string | null;
  is_current: boolean;
};

export type RiskOrganization = {
  organization_id: string | null;
  organization_name: string | null;
  location_count: number;
  scored_location_count: number;
  mean_score: number | null;
  total_studies: number;
  locations: RiskOrganizationLocation[];
};

export type SiteRiskPayload = {
  scored: boolean;
  reason?: string;
  organization?: RiskOrganization | null;
  /**
   * The trials this site participates in, resolved through the mastering graph.
   * Not the same as `MasteredSite.trials`, which is derived by matching the
   * golden id as a prefix of trial_site_id and is therefore empty for feeds that
   * build trial_site_id from the raw site id.
   */
  trial_ids?: string[];
  milestones: RiskMilestone[];
  score?: number | null;
  classification?: string | null;
  confidence?: number | null;
  historical_delay_rate?: number | null;
  avg_milestone_days?: number | null;
  studies_participated?: number | null;
  outstanding_milestone_count?: number | null;
  factors?: RiskFactor[];
  computed_date?: string | null;
};

const BAND_TONE: Record<string, string> = {
  high: "bg-[#FEE2E2] text-[#BE0303]",
  medium: "bg-[#FEF3C7] text-[#B45309]",
  low: "bg-[#DCFCE7] text-[#15803D]",
};

/** What a factor's 0-1 scale means, so its normalised value can be read. */
const FACTOR_SCALE: Record<string, string> = {
  isf_incompleteness:
    "Share of expected ISF documents still uncollected — 1.00 means nothing collected",
  milestone_outstanding_rate:
    "Share of startup milestones with no collected document — 1.00 means none met",
  open_query_rate:
    "Open monitor queries per collected document, capped at one query per document",
  experience_deficit:
    "How far short of five prior studies the site falls — 1.00 means no history",
};

/**
 * How the score is built, stated in the UI rather than left implicit.
 *
 * The weights are a deliberate judgment, not a fit: this feed carries no
 * activation outcomes to train or validate against, so anything fitted would be
 * fitted to nothing. That makes this a transparent ranking heuristic, useful for
 * ordering a site list and for showing which evidence drives a site's position —
 * not a calibrated probability. Say so on the page, because a bare 0-1 number
 * invites being read as one.
 */
const METHOD_NOTE =
  "Each factor is scaled 0-1 (1 = worst case), multiplied by a weight, and summed. " +
  "Weights start from equal thirds-of-attention and are set by judgment — ISF completeness 0.35, " +
  "startup milestones 0.30, open queries 0.20, study experience 0.15 — then re-normalised across " +
  "whichever factors a site actually has data for, so a missing input neither counts as zero risk " +
  "nor shrinks the score. Nothing here is fitted to outcomes: this feed has no activation dates to " +
  "train or validate against. Read it as a transparent ranking of where the evidence points, not as " +
  "a calibrated probability of delay — a predictive model would need invitation and activation " +
  "dates, and testing against them.";

/** Why a given supporting metric is blank — surfaced next to the gap itself. */
const UNAVAILABLE_REASON: Record<string, string> = {
  historical_delay_rate:
    "Needs site invitation and activation dates. The eISF feed does not carry them; they live in the study start-up feed.",
  avg_milestone_days:
    "Document file dates in the current extract are bulk-import timestamps, so expected-to-actual intervals are not measurable yet.",
};

export function useSiteRisk(siteKey: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["site-risk", siteKey],
    queryFn: () =>
      apiFetchJson<SiteRiskPayload>(
        `${import.meta.env.VITE_API_URL}/data/portfolio/site-risk/${encodeURIComponent(siteKey!)}/`,
      ),
    enabled: enabled && !!siteKey,
    staleTime: 5 * 60 * 1000,
  });
}

export function RiskBandChip({ classification }: { classification: string }) {
  const tone =
    BAND_TONE[classification.toLowerCase()] ?? "bg-[#E8EEF2] text-[#48636F]";
  return (
    <span
      className={`inline-flex h-5 items-center rounded-full px-2 text-[12px] font-semibold leading-5 ${tone}`}
    >
      {classification}
    </span>
  );
}

/** A metric value, or the reason it is missing. */
function MetricValue({
  value,
  suffix,
  unavailableKey,
}: {
  value: number | null | undefined;
  suffix?: string;
  unavailableKey?: string;
}) {
  if (value == null) {
    const reason = unavailableKey
      ? UNAVAILABLE_REASON[unavailableKey]
      : undefined;
    return (
      <span className="inline-flex items-center gap-1 text-[15px] text-[#B0BEC5]">
        —{reason ? <InfoTooltip title={reason} /> : null}
      </span>
    );
  }
  return (
    <span className="text-[15px] font-semibold text-[#1F2933] tabular-nums">
      {value}
      {suffix}
    </span>
  );
}

function MetricCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-[#8C8C8C]">
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Score, band, confidence and the requirement's three supporting metrics. */
export function RiskScoreCard({ risk }: { risk: SiteRiskPayload }) {
  if (!risk.scored) {
    return (
      <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-[#1F2933]">
            Not scored
          </span>
          <InfoTooltip title="Too little evidence to score this site: no document corpus or startup milestones resolve to it. This is a data gap, not a low-risk finding." />
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      {/* 6-up only from xl: between 1024 and 1280 the six tracks (minmax(0,1fr),
          so no min-content floor) squeeze below the score row's width and the
          info icon paints across the divider into the Confidence cell. */}
      <div className="grid grid-cols-2 divide-x divide-[#F0F2F4] border-b border-[#F0F2F4] md:grid-cols-3 xl:grid-cols-6">
        <MetricCell label="Delay risk score">
          <div className="flex flex-wrap items-center gap-2">
            <MetricValue
              value={risk.score != null ? Number(risk.score.toFixed(2)) : null}
            />
            {risk.classification ? (
              <RiskBandChip classification={risk.classification} />
            ) : null}
            <InfoTooltip title={METHOD_NOTE} />
          </div>
        </MetricCell>
        <MetricCell label="Confidence">
          <div className="flex items-center gap-1">
            <MetricValue
              value={
                risk.confidence != null
                  ? Number(risk.confidence.toFixed(2))
                  : null
              }
            />
            <InfoTooltip title="How much to trust the score: data completeness, number of contributing studies, and how much of the input is inferred rather than observed." />
          </div>
        </MetricCell>
        <MetricCell label="Historical delay rate">
          <MetricValue
            value={risk.historical_delay_rate}
            unavailableKey="historical_delay_rate"
          />
        </MetricCell>
        <MetricCell label="Avg milestone time">
          <MetricValue
            value={risk.avg_milestone_days}
            suffix=" d"
            unavailableKey="avg_milestone_days"
          />
        </MetricCell>
        <MetricCell label="Studies participated">
          <MetricValue value={risk.studies_participated} />
        </MetricCell>
        <MetricCell label="Milestones outstanding">
          <MetricValue value={risk.outstanding_milestone_count} />
        </MetricCell>
      </div>
    </div>
  );
}

/** Per-factor contributions — the explainability the requirement asks for. */
export function RiskFactorBreakdown({ factors }: { factors: RiskFactor[] }) {
  if (factors.length === 0) return null;
  const max = Math.max(...factors.map((f) => f.contribution), 0.0001);

  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[13px] font-semibold text-[#1F2933]">
          What drives this score
        </span>
        <InfoTooltip title="Ordered by how much each factor moves the score. Each row states the counts it was measured from; hover a share for the arithmetic behind it. Factors marked inferred are derived from document activity rather than read from a milestone feed." />
      </div>
      <div className="flex flex-col gap-3">
        {factors.map((factor) => (
          <div key={factor.code} className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-[#1F2933]">
                {factor.label}
                {factor.basis.startsWith("inferred") ? (
                  <span className="rounded bg-[#E8EEF2] px-1 py-0.5 text-[10px] font-normal text-[#48636F]">
                    inferred
                  </span>
                ) : null}
              </div>
              {/* The measured counts, which is what a reader can act on. */}
              <div className="mt-0.5 text-[11px] text-[#8C8C8C]">
                {factor.evidence ?? "—"}
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[#F0F2F4]">
                <div
                  className="h-full rounded-full bg-[#2B4752]"
                  style={{ width: `${(factor.contribution / max) * 100}%` }}
                />
              </div>
            </div>
            <div className="flex w-[110px] shrink-0 items-center justify-end gap-1 pt-0.5">
              <span className="text-[13px] font-semibold tabular-nums text-[#1F2933]">
                {factor.share != null
                  ? `${Math.round(factor.share * 100)}%`
                  : "—"}
              </span>
              <span className="text-[11px] text-[#8C8C8C]">of score</span>
              <InfoTooltip
                title={`${FACTOR_SCALE[factor.code] ?? "Scaled 0-1, where 1 is the worst case"}. Here that scales to ${factor.value.toFixed(2)}, weighted ${factor.weight.toFixed(2)} → ${factor.contribution.toFixed(3)} of a total score of ${factors
                  .reduce((n, f) => n + f.contribution, 0)
                  .toFixed(
                    3,
                  )}, i.e. ${Math.round((factor.share ?? 0) * 100)}% of it.`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Organization tier — the top level of the requirement's hierarchy
 * (study-site → protocol → physical site → organization).
 *
 * The roll-up is only as meaningful as the source's organization graph. When a
 * feed masters every site to its own institution the organization has exactly
 * one location, and the panel says so rather than presenting a one-site average
 * as a network view.
 *
 * `organization_name` is deliberately NOT displayed. In the eISF feed
 * `golden_site.canonical_institution` is vendor-anonymised to generated business
 * phrases ("focused even-keeled encryption") exactly as document titles are, so
 * showing it puts noise where a reader expects an institution. Same call as
 * `docLabel` in components/Documents/tmf.tsx: name the thing from something real
 * — here the mastered location(s) — rather than print an anonymised value.
 */
export function RiskOrganizationPanel({
  organization,
}: {
  organization: RiskOrganization;
}) {
  const single = organization.location_count <= 1;
  const thisSite =
    organization.locations.find((l) => l.is_current) ??
    organization.locations[0];

  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[13px] font-semibold text-[#1F2933]">
          Organization
        </span>
        <InfoTooltip title="The parent institution this site belongs to, and every other location mastered under it. Organization-level performance is a roll-up of its locations — strong performance at one department does not imply the whole institution." />
      </div>

      <div className="mb-3 flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <span className="inline-flex items-baseline gap-1.5 text-[14px] font-semibold text-[#1F2933]">
          {single ? (thisSite?.site_name ?? "—") : "Parent organization"}
          <InfoTooltip title="The source feed does not provide a usable organization name — the institution name it carries is vendor-anonymised — so the organization is identified by its mastered location instead. A master site registry with real organization identifiers is on the data-ask list." />
        </span>
        <span className="text-[12px] text-[#8C8C8C]">
          {organization.location_count} location
          {organization.location_count === 1 ? "" : "s"} ·{" "}
          {organization.total_studies} stud
          {organization.total_studies === 1 ? "y" : "ies"}
        </span>
        <span className="inline-flex items-center gap-1 text-[12px] text-[#48636F]">
          Mean risk{" "}
          <span className="font-semibold tabular-nums">
            {organization.mean_score != null
              ? organization.mean_score.toFixed(2)
              : "—"}
          </span>
          <InfoTooltip
            title={
              organization.mean_score == null
                ? "No location under this organization has a score yet."
                : `Averaged over ${organization.scored_location_count} scored location${organization.scored_location_count === 1 ? "" : "s"}; unscored locations are excluded rather than counted as zero.`
            }
          />
        </span>
      </div>

      {single ? (
        <div className="flex items-center gap-1 rounded-[6px] bg-[#F7F9FA] px-3 py-2 text-[12px] text-[#48636F]">
          This organization has a single mastered location, so the organization
          roll-up equals the site.
          <InfoTooltip title="The source feed masters each site to its own institution, so there are no sibling locations to aggregate. A registry with shared parent organizations (or organization identifiers on the site records) is what makes this tier meaningful." />
        </div>
      ) : (
        <div className="flex flex-col divide-y divide-[#F0F2F4]">
          {organization.locations.map((location) => (
            <div
              key={location.site_key}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <div className="truncate text-[13px] text-[#1F2933]">
                  {location.site_name ?? location.site_key}
                  {location.is_current ? (
                    <span className="ml-2 rounded bg-[#E8EEF2] px-1.5 py-0.5 text-[10px] font-semibold text-[#48636F]">
                      this site
                    </span>
                  ) : null}
                </div>
                <div className="truncate text-[11px] text-[#8C8C8C]">
                  {[location.city, location.country]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-[12px] tabular-nums text-[#1F2933]">
                  {location.score != null ? location.score.toFixed(2) : "—"}
                </span>
                {location.classification ? (
                  <RiskBandChip classification={location.classification} />
                ) : (
                  <span className="text-[11px] text-[#B0BEC5]">not scored</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MILESTONE_COLUMNS: ColumnsType<RiskMilestone> = [
  {
    title: "Milestone",
    dataIndex: "label",
    key: "label",
    width: 200,
    ellipsis: true,
  },
  { title: "Trial", dataIndex: "trial_id", key: "trial_id", width: 130 },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 130,
    render: (status: string) => (
      <span
        className={`inline-flex h-5 items-center rounded-full px-2 text-[11px] font-medium leading-5 ${
          status === "collected"
            ? "bg-[#DCFCE7] text-[#15803D]"
            : "bg-[#FEF3C7] text-[#B45309]"
        }`}
      >
        {status === "collected" ? "Collected" : "Outstanding"}
      </span>
    ),
  },
  {
    title: () => (
      <span className="inline-flex items-center gap-1">
        Expected from
        <InfoTooltip title="When the milestone became expected: the earliest creation of a document slot for it in the site's binder template." />
      </span>
    ),
    dataIndex: "expected_date",
    key: "expected_date",
    width: 150,
    render: (date: string | null) =>
      date ? (
        date.slice(0, 10)
      ) : (
        <span className="inline-flex items-center gap-1 text-[#B0BEC5]">
          —
          <InfoTooltip title="No document slot was templated for this milestone, so there is no date on which it became expected." />
        </span>
      ),
  },
  {
    // Named for what the date is rather than "Actual": in the current extract
    // every collected file carries an upload timestamp inside one 15-minute
    // bulk-import window, so this is when the document arrived in eBinders, not
    // when the site filed it. Labelling it "Actual" would read as a milestone
    // date and invite a latency calculation the data cannot support.
    title: () => (
      <span className="inline-flex items-center gap-1">
        File landed
        <InfoTooltip title="When a document satisfying this milestone arrived in eBinders. In the current extract every collected file was loaded in one bulk import, so these dates are all the same and are NOT the dates the site filed the documents — which is why elapsed time is not shown. Real filing dates need the study start-up feed or a populated document date." />
      </span>
    ),
    dataIndex: "actual_date",
    key: "actual_date",
    width: 150,
    render: (date: string | null) =>
      date ? date.slice(0, 10) : <span className="text-[#B0BEC5]">—</span>,
  },
  {
    title: "Documents",
    key: "documents",
    width: 120,
    render: (_: unknown, row: RiskMilestone) =>
      `${row.collected_documents ?? 0} / ${row.expected_documents ?? 0}`,
  },
];

/** Expected-vs-actual startup milestones behind the score. */
export function RiskMilestoneTable({
  milestones,
}: {
  milestones: RiskMilestone[];
}) {
  if (milestones.length === 0) {
    return (
      <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 text-[13px] text-[#8C8C8C] shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
        No startup milestones resolve to this site.
      </div>
    );
  }
  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white p-4 shadow-[0_8px_24px_rgba(15,23,42,0.08)]">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-[13px] font-semibold text-[#1F2933]">
          Startup milestones
        </span>
        <InfoTooltip title="Classified from the ISF folder taxonomy: a templated document slot marks the milestone as expected, a collected file marks it met. Contract/CTA is absent because this taxonomy does not name it." />
      </div>
      <Table<RiskMilestone>
        columns={MILESTONE_COLUMNS}
        dataSource={milestones}
        rowKey={(row) => `${row.trial_id}-${row.code}`}
        size="small"
        pagination={false}
        scroll={{ x: 840 }}
      />
    </div>
  );
}
